import React, { useState } from 'react'
import { Button, Card, Flex, Space, Statistic, Typography } from 'antd'
import { CheckCircleOutlined, GlobalOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { ConnectionCard } from '../../facebook/components/connection-card'
import { CandidateModal } from '../../candidate/components/candidate-modal'
import { Link, Navigate } from '@tanstack/react-router'
import { useAuth } from '../../auth'
import { StartChoice } from '../../components/start-choice'
import { useSites } from '../../site/hooks'
import { useCustomerDashboard } from '../hooks'
import {
    CONNECT_SITE,
    FACEBOOK_DISCONNECT,
    INDEX_SITE,
    INVITE_COMPANY_USER,
    SHOW_COMPANY_USAGE,
    STORE_CAMPAIGN,
} from '../../user/permission'
import { UsageProgress } from '../../components/usage-progress'
import { DashboardAttention } from './dashboard-attention'
import { DashboardSitesCard } from './dashboard-sites-card'
import { DashboardCandidatesCard } from './dashboard-candidates-card'

const { Title, Text } = Typography

export function CustomerDashboard() {
    const { t } = useTranslation()

    const { hasSubscription, can, onTrial, activeCompany } = useAuth()
    const [candidateId, setCandidateId] = useState<string | undefined>(undefined)
    const dashboard = useCustomerDashboard({
        enabled: hasSubscription,
    })
    const sites = useSites(can(INDEX_SITE))

    // A company that added a website chose the chat: its home is the websites, where it also chooses the plan.
    if (!hasSubscription && sites.data?.length) {
        return <Navigate to="/websites" />
    }

    if (!hasSubscription) {
        return (
            <StartChoice
                footer={
                    <Link
                        to="/settings"
                        className="text-primary"
                    >
                        {t('See the plans first')}
                    </Link>
                }
            />
        )
    }

    const period = dashboard.data?.period
    const hasSites = !!sites.data?.length

    const counters = [
        {
            title: t('New candidates'),
            value: period?.candidates,
            prefix: <UserOutlined />,
        },
        {
            title: t('Interviews finished'),
            value: period?.finished,
            prefix: <CheckCircleOutlined />,
            note:
                period && period.finished > 0 ? t('{{count}} good matches', { count: period.goodMatches }) : undefined,
        },
        ...(hasSites && period?.chats !== undefined
            ? [
                  {
                      title: t('Website chats'),
                      value: period.chats,
                      prefix: <GlobalOutlined />,
                  },
              ]
            : []),
        ...(dashboard.data?.users !== undefined
            ? [
                  {
                      title: t('Colleagues'),
                      value: dashboard.data.users,
                      prefix: <TeamOutlined />,
                  },
              ]
            : []),
    ]

    return (
        <Flex
            gap="large"
            vertical
        >
            <Title
                level={3}
                className="mb-0"
            >
                {t('Dashboard')}
            </Title>

            {dashboard.data && (
                <DashboardAttention
                    attention={dashboard.data.attention}
                    sites={sites.data ?? []}
                />
            )}

            <Flex
                vertical
                gap="small"
            >
                <Text type="secondary">{onTrial ? t('In your free trial') : t('This billing period')}</Text>
                <Flex
                    className="w-full grid grid-cols-2 lg:grid-cols-4"
                    gap="large"
                >
                    {counters.map((counter) => (
                        <Card
                            key={counter.title}
                            className="w-full"
                        >
                            <Statistic
                                loading={dashboard.isPending}
                                title={counter.title}
                                value={counter.value}
                                prefix={counter.prefix}
                            />
                            {counter.note && (
                                <Text
                                    type="secondary"
                                    className="text-xs"
                                >
                                    {counter.note}
                                </Text>
                            )}
                        </Card>
                    ))}
                </Flex>
            </Flex>

            {can(SHOW_COMPANY_USAGE) && (
                <UsageProgress
                    usage={dashboard.data?.usage}
                    limit={dashboard.data?.limit}
                    overageUsage={dashboard.data?.overageUsage}
                    overageUnitAmount={dashboard.data?.overageUnitAmount}
                    trial={onTrial}
                    trialEndsAt={activeCompany?.trialEndsAt}
                />
            )}

            <Flex
                align="start"
                gap="large"
                className="w-full flex-col lg:flex-row"
            >
                <div className="w-full lg:flex-[3]">
                    <DashboardCandidatesCard onOpenCandidate={setCandidateId} />
                </div>

                <Flex
                    vertical
                    gap="large"
                    className="w-full lg:flex-[2]"
                >
                    {hasSites && <DashboardSitesCard sites={sites.data ?? []} />}

                    {/* Only for a Facebook lead form: other customers never need the connection. */}
                    {dashboard.data?.usesLeadForms && can(FACEBOOK_DISCONNECT) && (
                        <ConnectionCard
                            title={t('Facebook lead forms')}
                            className="w-full"
                            disconnect={false}
                        />
                    )}

                    <Card
                        title={t('Quick Actions')}
                        className="w-full"
                    >
                        <Space
                            direction="vertical"
                            className="w-full"
                        >
                            {can(STORE_CAMPAIGN) && (
                                <Link to="/campaigns/create">
                                    <Button
                                        type="primary"
                                        block
                                        size="large"
                                    >
                                        {t('Add {{name}}', { name: t('Campaign') })}
                                    </Button>
                                </Link>
                            )}
                            {can(CONNECT_SITE) && (
                                <Link
                                    to="/websites"
                                    search={{ add: true }}
                                >
                                    <Button
                                        block
                                        size="large"
                                    >
                                        {t('Add website')}
                                    </Button>
                                </Link>
                            )}
                            {can(INVITE_COMPANY_USER) && dashboard.data?.canInvite && (
                                <Link
                                    to="/colleagues"
                                    search={{ invite: true }}
                                >
                                    <Button
                                        block
                                        size="large"
                                    >
                                        {t('Invite colleague')}
                                    </Button>
                                </Link>
                            )}
                        </Space>
                    </Card>
                </Flex>
            </Flex>

            <CandidateModal
                candidateId={candidateId}
                open={!!candidateId}
                onCancel={() => {
                    setCandidateId(undefined)
                }}
            />
        </Flex>
    )
}
